// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC} from 'react'
import {useIntl} from 'react-intl'
import {Link, useNavigate} from 'react-router-dom'
import {IClientDto} from '../Client.model'
import {PreviewCard} from '../../../common/component/PreviewCard'
import { SettingOption } from '../../../common/component/DropdownMenu'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { parseLocalDate } from '../../../util/Locale'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'

interface Props {
  client?: IClientDto
  loading: boolean
  setRowSelected: (rowId?: string) => void
}

const ClientPreview: FC<Props> = ({client, loading, setRowSelected}: Props) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.DETAILS'}),
      icon: 'bi-book',
      action: () => navigate(`/clients/${client?.id}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.EDIT'}),
      icon: 'bi-pencil',
      action: () => navigate(`/clients/${client?.id}/edit`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.CONTRACT'}),
      icon: 'bi-vector-pen',
      action: () => navigate(`/contracts/new?clientId=${client?.id}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.ASSET'}),
      icon: 'bi-cpu',
      action: () => navigate(`/assets/new?clientId=${client?.id}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.REPORT'}),
      icon: 'bi-speedometer',
      action: () => navigate(`/reports/new?clientId=${client?.id}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.TICKET'}),
      icon: 'bi-wrench-adjustable-circle',
      action: () => navigate(`/tickets/new?clientId=${client?.id}`),
    },
  ]

  return (
    <PreviewCard
      loading={loading}
      title={client?.displayName}
      subTitle={client?.sequence}
      pathOnClickHandler={`/clients/${client?.id}`}
      closeOnClickHandler={() => setRowSelected(undefined)}
      settingOptions={settingOptions}
      content={
        client && (
          <div>
            <div className='row my-10'>
              <SubSectionHeader
                title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                separator={false}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                    value={parseLocalDate(
                      client.updatedAt,
                      intl.formatMessage({id: 'I18N.LOCALE'})
                    )}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ID'})}
                    value={`${client.sequence}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.DISPLAY_NAME'})}
                    object={
                      <span className='fs-6 ms-3 text-dark'>
                        <Link className='' to={`/clients/${client.id}`}>
                          {client.displayName}
                        </Link>
                      </span>
                    }
                  ></CustomInputRenderer>

                  {client.sector && (
                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.SECTOR'})}
                      value={client.sector}
                    ></CustomInputRenderer>
                  )}
                </div>
              </div>

              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.LOCATION'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                    value={
                      client.countryCode
                        ? intl.formatMessage({
                            id: `DROPDOWN.COUNTRY.${client.countryCode}`,
                          })
                        : ''
                    }
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                    value={client.address}
                  ></CustomInputRenderer>
                </div>
              </div>

              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.METRICS'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.CONTRACTS'})}
                    value={`${client.totalContract || 0}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ASSETS'})}
                    value={`${client.totalAsset || 0}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.REPORTS'})}
                    value={`${client.totalReport || 0}`}
                  ></CustomInputRenderer>
                </div>
              </div>
            </div>
          </div>
        )
      }
    ></PreviewCard>
  )
}

export {ClientPreview}
